// ============================================================================
// RHSolicitacoes_Enviar.js
// Envio do documento preenchido/assinado — UMA tela para os nove tipos.
//
// O que varia entre eles é só o campo de período, e isso vem do catálogo
// (`periodicidade`), não de um `if` por tipo escrito aqui:
//   • EVENTO → NÃO PERGUNTA NADA. A DATA_REFERENCIA é a de HOJE, preenchida
//     sozinha (decisão do RH em 15/09/2026 — ver o bloco abaixo).
//   • MENSAL → pede MÊS/ANO, e isso CONTINUA sendo uma escolha: o documento de
//     agosto costuma ser enviado em setembro, então "hoje" acertaria o mês
//     errado. É UM documento com todas as datas do mês e uma assinatura; o
//     banco trava um vivo por funcionário/competência.
//
// ⚠️⚠️ O CAMPO "Data a que o documento se refere" FOI REMOVIDO (15/09/2026), e
//    o efeito colateral é real: a DATA_REFERENCIA dos tipos de EVENTO passa a
//    ser a data do ENVIO, não mais a do FATO. Antes dava para registrar na
//    sexta uma advertência aplicada na terça e o banco guardava a terça; agora
//    guarda a sexta. Foi decisão do RH — um campo a menos na mão do gerente, e
//    a data do fato já está escrita no papel anexado. Se um dia a diferença
//    voltar a importar (relatório por data do fato, prazo legal), é aqui que o
//    campo volta, e os registros a partir desta data não terão a distinção.
//
// ⚠️ O anexo é OBRIGATÓRIO: neste módulo o papel é o produto. Sem ele não há o
//    que registrar, e o backend recusa.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CalendarDays, CheckCircle2, FileUp, Loader2, Paperclip, Search } from 'lucide-react';
import { estilos } from '../../processoseletivo/psUI';
import EscolherAnexo from '../../EscolherAnexo';

const COR = '#e11d48';

// Espelha o multer do backend (TIPOS_ANEXO / TAMANHO_MAX_MB). Aqui é só
// gentileza com o usuário — quem recusa de verdade é o servidor.
const ACEITA = 'application/pdf,image/jpeg,image/png,image/webp';
const TAMANHO_MAX_MB = 15;

// 'AAAA-MM-DD' de hoje, no fuso LOCAL. ⚠️ Não é `toISOString().slice(0,10)`:
// aquele é UTC e, depois das 21h em Brasília, devolve o dia seguinte — o
// documento enviado à noite nasceria datado de amanhã.
function hojeLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// 'AAAA-MM' do mês anterior: o documento mensal quase sempre é fechado depois
// que o mês acaba, então é o padrão que economiza um clique.
function competenciaPadrao() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// 'AAAA-MM-DD' → 'DD/MM/AAAA', só para o aviso na tela.
const fmtDataBr = (iso) => iso.split('-').reverse().join('/');

function RHSolicitacoesEnviar({ tipo, aoEnviar }) {
  const ehMensal = tipo.periodicidade === 'MENSAL';
  // Quantos documentos este tipo aceita (1 na maioria; só a Falta de Marcação
  // passa disso hoje). Vem do catálogo pelo backend — a tela não tem cópia.
  const maxAnexos = Number(tipo.maxAnexos) > 1 ? Number(tipo.maxAnexos) : 1;
  const multi = maxAnexos > 1;

  const [funcionarios, setFuncionarios] = useState([]);
  const [busca, setBusca] = useState('');
  const [idFuncionario, setIdFuncionario] = useState('');
  const [competencia, setCompetencia] = useState(competenciaPadrao());
  const [observacao, setObservacao] = useState('');
  // Lista de anexos (o backend chama o 1º de PRINCIPAL). Para os tipos de um só,
  // é sempre uma lista de 0 ou 1 — o comportamento antigo, sem ramo especial.
  const [arquivos, setArquivos] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const carregarFuncionarios = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch('/api/rh/solicitacoes/funcionarios', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Não foi possível carregar os funcionários.');
      setFuncionarios(await resp.json());
    } catch (e) {
      setFeedback({ tipo: 'erro', msg: e.message });
    }
  }, []);

  useEffect(() => { carregarFuncionarios(); }, [carregarFuncionarios]);

  // Lista filtrada pela busca — NOME ou CPF, igual à Atualização de Cadastro.
  // ⚠️ O CPF entra na busca porque homônimo é comum na rede (a dtbConfere tem
  //    casos de nome idêntico entre ativos); o nome sozinho não desempata.
  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return funcionarios;
    return funcionarios.filter(
      (f) => (f.nome || '').toLowerCase().includes(q) || (f.cpf || '').includes(q)
    );
  }, [funcionarios, busca]);

  // Recebe o File direto do EscolherAnexo (que já limpa o input para permitir
  // reescolher o mesmo arquivo depois de um erro de tamanho). Nos tipos de um
  // só, SUBSTITUI; nos de vários, ACRESCENTA até o teto.
  const escolherArquivo = (f) => {
    setFeedback(null);
    if (!f) return;
    if (f.size > TAMANHO_MAX_MB * 1024 * 1024) {
      setFeedback({ tipo: 'erro', msg: `Arquivo muito grande (máximo ${TAMANHO_MAX_MB} MB).` });
      return;
    }
    setArquivos((atual) => {
      if (!multi) return [f];
      if (atual.length >= maxAnexos) {
        setFeedback({ tipo: 'erro', msg: `No máximo ${maxAnexos} documentos por solicitação.` });
        return atual;
      }
      return [...atual, f];
    });
  };

  const removerArquivo = (idx) => {
    setFeedback(null);
    setArquivos((atual) => atual.filter((_, i) => i !== idx));
  };

  const enviar = async (e) => {
    e.preventDefault();
    if (enviando) return;
    setFeedback(null);

    if (!idFuncionario) return setFeedback({ tipo: 'erro', msg: 'Selecione o funcionário.' });
    if (ehMensal && !competencia) return setFeedback({ tipo: 'erro', msg: 'Informe o mês do documento.' });
    if (!arquivos.length) return setFeedback({ tipo: 'erro', msg: 'Anexe o documento preenchido e assinado.' });

    setEnviando(true);
    try {
      // multipart: é upload de arquivo — nada de JSON aqui.
      const corpo = new FormData();
      corpo.append('tipo', tipo.chave);
      corpo.append('idFuncionario', idFuncionario);
      // ⚠️ A data do evento é calculada AQUI, na hora do envio — nunca num
      //    estado montado quando a tela abriu: a aba fica aberta a manhã
      //    inteira, e uma virada de meia-noite gravaria o dia anterior.
      if (ehMensal) corpo.append('competencia', competencia);
      else corpo.append('dataReferencia', hojeLocal());
      if (observacao.trim()) corpo.append('observacao', observacao.trim());
      // Campo 'arquivos' (plural): o backend aceita 1 ou vários por aqui. O 1º
      // vira o documento principal da solicitação.
      arquivos.forEach((a) => corpo.append('arquivos', a));

      const token = localStorage.getItem('authToken');
      const resp = await fetch('/api/rh/solicitacoes', {
        method: 'POST',
        // ⚠️ SEM Content-Type: o browser precisa montar o boundary do multipart.
        //    Declará-lo à mão quebra o upload.
        headers: { Authorization: `Bearer ${token}` },
        body: corpo,
      });
      const dados = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(dados.message || 'Erro ao enviar a solicitação.');

      setFeedback({ tipo: 'ok', msg: 'Documento enviado. O RH foi avisado por e-mail.' });
      setTimeout(() => aoEnviar && aoEnviar(), 900);
    } catch (e2) {
      setFeedback({ tipo: 'erro', msg: e2.message });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={estilos.tituloSecao}>{tipo.rotulo}</h2>
      <p style={estilos.subtituloSecao}>
        {ehMensal
          ? 'Um documento por mês, com todas as datas e a assinatura do colaborador.'
          : 'Anexe o documento preenchido e assinado.'}
      </p>

      <form onSubmit={enviar} style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div>
          <label style={estilos.rotuloFiltro}>Funcionário</label>
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <Search size={16} style={{ position: 'absolute', left: 12, top: 12, color: '#94a3b8' }} />
            <input
              style={{ ...estilos.input, paddingLeft: 34 }}
              placeholder="Buscar por nome ou CPF…"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          <select
            value={idFuncionario}
            onChange={(e) => setIdFuncionario(e.target.value)}
            style={estilos.input}
          >
            <option value="">Selecione…</option>
            {filtrados.map((f) => (
              <option key={f.idFuncionario} value={f.idFuncionario}>
                {f.nome}{f.cpf ? ` · ${f.cpf}` : ''} — {f.cargo || 'sem cargo'} ({f.loja})
              </option>
            ))}
          </select>
          {busca && filtrados.length === 0 && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: '#94a3b8' }}>Nenhum funcionário encontrado.</p>
          )}
        </div>

        {/* MENSAL ainda PERGUNTA o mês: o documento de agosto é enviado em
            setembro, então "hoje" acertaria o mês errado. EVENTO não pergunta
            nada — só informa a data que será gravada, porque o gerente tem de
            saber o que está registrando (nada de campo invisível). */}
        {ehMensal ? (
          <div>
            <label style={estilos.rotuloFiltro}>Competência (mês do documento)</label>
            <input type="month" value={competencia} onChange={(e) => setCompetencia(e.target.value)} style={estilos.input} />
          </div>
        ) : (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
            borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0',
            fontSize: 13, color: '#64748b',
          }}>
            <CalendarDays size={16} color="#94a3b8" />
            <span>Será registrado com a data de hoje, <strong style={{ color: '#0f172a' }}>{fmtDataBr(hojeLocal())}</strong>.</span>
          </div>
        )}

        <div>
          <label style={estilos.rotuloFiltro}>Observação (opcional)</label>
          <textarea
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            rows={3}
            placeholder="Algo que o RH precise saber além do que está no documento."
            style={{ ...estilos.input, resize: 'vertical' }}
          />
        </div>

        <div>
          <label style={estilos.rotuloFiltro}>
            {multi ? 'Documentos assinados (PDF ou foto)' : 'Documento assinado (PDF ou foto)'}
          </label>

          {/* Lista dos arquivos já escolhidos. Nos tipos de um só, tem no máximo
              uma linha; nos de vários, uma por documento, com o "x" para tirar. */}
          {arquivos.map((a, i) => (
            <div key={`${a.name}-${i}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', marginBottom: 8,
                border: `1px solid ${COR}`, borderRadius: 10, background: '#fff1f2', flexWrap: 'wrap',
              }}
            >
              <Paperclip size={18} color={COR} />
              <span style={{ fontSize: 14, color: '#0f172a', fontWeight: 700, wordBreak: 'break-all' }}>
                {multi ? `${i + 1}. ` : ''}{a.name}
              </span>
              <button
                type="button"
                onClick={() => removerArquivo(i)}
                style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}
              >
                Remover
              </button>
            </div>
          ))}

          {/* Botão de escolha: fica escondido quando o tipo é de um só e já há
              arquivo (aí a linha acima traz "Remover"); nos de vários, some ao
              atingir o teto. */}
          {arquivos.length < maxAnexos && (
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '16px',
                border: '2px dashed #cbd5e1', borderRadius: 10, background: '#f8fafc', flexWrap: 'wrap',
              }}
            >
              <Paperclip size={20} color="#94a3b8" />
              <span style={{ fontSize: 14, color: '#64748b' }}>
                {multi ? `Até ${maxAnexos} documentos, ${TAMANHO_MAX_MB} MB cada` : `Até ${TAMANHO_MAX_MB} MB`}
              </span>
              <div style={{ marginLeft: 'auto' }}>
                <EscolherAnexo
                  accept={ACEITA}
                  cor={COR}
                  rotulo={arquivos.length > 0 ? 'Adicionar outro' : 'Escolher arquivo'}
                  onArquivo={escolherArquivo}
                />
              </div>
            </div>
          )}
        </div>

        {feedback && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 10, fontSize: 14,
            background: feedback.tipo === 'ok' ? '#ecfdf5' : '#fef2f2',
            border: `1px solid ${feedback.tipo === 'ok' ? '#a7f3d0' : '#fecaca'}`,
            color: feedback.tipo === 'ok' ? '#047857' : '#b91c1c',
          }}>
            {feedback.tipo === 'ok' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
            {feedback.msg}
          </div>
        )}

        <button
          type="submit"
          disabled={enviando}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            alignSelf: 'flex-start', padding: '12px 28px', borderRadius: 8, border: 'none',
            background: enviando ? '#fda4af' : COR, color: '#fff', fontWeight: 800, fontSize: 15,
            cursor: enviando ? 'default' : 'pointer',
          }}
        >
          {enviando ? <Loader2 size={18} className="animate-spin" /> : <FileUp size={18} />}
          {enviando ? 'Enviando…' : 'Enviar ao RH'}
        </button>
      </form>
    </div>
  );
}

export default RHSolicitacoesEnviar;
